import { createSlice } from '@reduxjs/toolkit';
import { LearningPathwayState, LearningPathwayStateData } from 'src/types/state/learningPathway';
import ClientLogger from 'src/utils/logger';
import { ClientLog } from 'src/constants/clientLog';
import { suApiSlice } from './api/su';
import { LearningPathwayStaticApiData } from '../../types/api/learningPathwayStatic';
import type { AppState } from '../store';

// State of the Learning Pathways Slice
export interface LearningPathwaysSliceState {
  byId: Record<string, LearningPathwayState>
  // Store for additional learning pathway metadata
}

export const initialState: LearningPathwaysSliceState = {
  byId: {},
};

export const learningPathwaysSlice = createSlice({
  name: 'learningPathways',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // Helper function to process learning pathway data into state
    const processLearningPathwayData = (
      state: LearningPathwaysSliceState,
      learningPathwayData: LearningPathwayStaticApiData,
    ) => {
      try {
        if (!learningPathwayData || !learningPathwayData.learningPathwayId) {
          ClientLogger.error(ClientLog.IllegalInternalDataState, {
            payload: learningPathwayData,
            url: window.location.href,
          });
          return;
        }

        const stateData: LearningPathwayStateData = {
          ...learningPathwayData,
          imageSrc: learningPathwayData.backgroundImage,
          sections: learningPathwayData.sections?.map((section) => ({
            ...section,
            learningModuleIds: section.moduleList?.map((module) => module.moduleId) || [],
          })) || [],
        };

        state.byId[learningPathwayData.learningPathwayId] = { data: stateData };
      } catch (error) {
        ClientLogger.error(ClientLog.PayloadProcessingError, {
          learningPathwayId: learningPathwayData.learningPathwayId,
          url: window.location.href,
        });
      }
    };

    builder
      .addMatcher(
        suApiSlice.endpoints.getLearningPathway.matchFulfilled,
        (state, action) => {
          processLearningPathwayData(state, action.payload.learningPathway);
        },
      )
      .addMatcher(
        suApiSlice.endpoints.getLearningPathwayStatic.matchFulfilled,
        (state, action) => {
          // Process learning pathway data into our store from the GET LearningPathwayStatic API
          const getLearningPathwaysById = (learningPathwayData: LearningPathwayStaticApiData) => {
            try {
              const learningPathwaysById: Record<string, LearningPathwayState> = {};

              // Validate required fields
              if (!learningPathwayData || !learningPathwayData.learningPathwayId) {
                ClientLogger.error(ClientLog.IllegalInternalDataState, {
                  payload: learningPathwayData,
                  url: window.location.href,
                });
                return learningPathwaysById;
              }

              const stateData: LearningPathwayStateData = {
                ...learningPathwayData,
                imageSrc: learningPathwayData.backgroundImage,
                sections: learningPathwayData.sections?.map((section) => {
                  const learningModuleIds = section.moduleList
                    ?.map((module) => module.moduleId) || [];

                  return {
                    ...section,
                    learningModuleIds,
                  };
                }) || [],
              };

              // Store the pathway data
              learningPathwaysById[learningPathwayData.learningPathwayId] = {
                data: stateData,
              };

              return learningPathwaysById;
            } catch (error) {
              ClientLogger.error(ClientLog.PayloadProcessingError, {
                learningPathwayId: learningPathwayData.learningPathwayId,
                url: window.location.href,
              });
              return {};
            }
          };

          state.byId = {
            ...state.byId,
            ...getLearningPathwaysById(action.payload.learningPathway),
          };
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listLearningPathways.matchFulfilled,
        (state, action) => {
          // Process learning pathways list data into our store
          try {
            action.payload.learningPathways.forEach((pathwayData: LearningPathwayStaticApiData) => {
              processLearningPathwayData(state, pathwayData);
            });
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, {
              url: window.location.href,
            });
          }
        },
      )
      .addMatcher(
        suApiSlice.endpoints.listLearningPathwaysPoly.matchFulfilled,
        (state, action) => {
          // Process PolyD learning pathways list data into our store
          try {
            action.payload.learningPathways.forEach((pathwayData: LearningPathwayStaticApiData) => {
              processLearningPathwayData(state, pathwayData);
            });
          } catch (error) {
            ClientLogger.error(ClientLog.PayloadProcessingError, { url: window.location.href });
          }
        },
      );
    // Add more matchers that may react to another slice's actions/APIs
  },
});

// Basic selectors
export const learningPathwaysActions = learningPathwaysSlice.actions;
export const selectLearningPathwayById = (
  state: AppState,
  learningPathwayId: string,
) => {
  return state.learningPathways.byId[learningPathwayId];
};

// Selectors for learning pathways list
export const selectAllLearningPathways = (state: AppState) => {
  return Object.values(state.learningPathways.byId);
};
